import { DEFAULT_ENV_FILE_PATTERNS, PROTECTED_DIR_RULE_PREFIX, PROTECTED_FOLDERS, SECRET_FILE_BUILTIN_RULE_SENTENCE, SECRET_FILE_ENV_RULE_SENTENCE, SECRET_FILE_ENV_TEMPLATE, SECRET_FILE_NO_EVENT_WHAT, SECRET_FILE_RULE_SENTENCE, } from '../../data/alert-risk-text';
import { clientDisplayName, fallbackOf, fillRiskTemplate, MAX_MATCHED_LENGTH, present, riskCopy, } from './common';
const SSH_PRIVATE_KEY_BASENAMES = new Set([
    'id_rsa',
    'id_dsa',
    'id_ecdsa',
    'id_ed25519',
    'id_ecdsa_sk',
    'id_ed25519_sk',
]);
/** File roles by `<folder>/<basename>` (rows 69–76). */
const ROLE_FILES = [
    ['.ssh/config', 'D3'],
    ['.ssh/known_hosts', 'D4'],
    ['.ssh/authorized_keys', 'D5'],
    ['.aws/credentials', 'D6'],
    ['.aws/config', 'D7'],
    ['.kube/config', 'D8'],
    ['.docker/config.json', 'D9'],
    ['.git/config', 'D10'],
];
/**
 * The file's role from its basename and parent folders, first match wins
 * (D1…D14). Segments must match exactly; backslashes become `/` and case is
 * ignored, as the agent matches. `partial` marks a folder name that only the
 * agent's substring match catches (e.g. `my_node_modules/`), or a location
 * longer than `MAX_MATCHED_LENGTH`; both render the static fallback.
 */
export function selectSecretFileVariant(location, ruleId) {
    if (location.length > MAX_MATCHED_LENGTH)
        return 'partial';
    const path = location.trim().replace(/\\/g, '/').toLowerCase();
    const segments = path.split('/').filter(s => s.length > 0);
    const basename = segments[segments.length - 1] ?? '';
    const hasSegment = (name) => segments.includes(name);
    if (SSH_PRIVATE_KEY_BASENAMES.has(basename))
        return { id: 'D1' };
    if (basename.endsWith('.pub') &&
        !basename.endsWith('-cert.pub') &&
        hasSegment('.ssh')) {
        return { id: 'D2' };
    }
    // Compared on the cleaned segments, so a trailing slash still matches.
    const joined = segments.join('/');
    for (const [file, id] of ROLE_FILES) {
        if (joined === file || joined.endsWith(`/${file}`))
            return { id };
    }
    // Row 77: the basename is .env or starts with .env. (not every name the
    // detector's env patterns match, such as prod.env). The protected .env.d/
    // folder itself is D14 (row 80).
    if (basename !== '.env.d' &&
        (basename === '.env' || basename.startsWith('.env.'))) {
        return { id: 'D11' };
    }
    if (ruleId != null &&
        ruleId.length > 0 &&
        !ruleId.startsWith(PROTECTED_DIR_RULE_PREFIX) &&
        !DEFAULT_ENV_FILE_PATTERNS.includes(ruleId)) {
        return { id: 'D12' };
    }
    if (hasSegment('node_modules'))
        return { id: 'D13' };
    // Row 80: {dir} is the first path segment that is a protected folder.
    const dir = segments
        .map(s => `${s}/`)
        .find(d => PROTECTED_FOLDERS.some(f => f.dir === d));
    if (dir != null)
        return { id: 'D14', dir };
    const partial = path.includes('node_modules/') ||
        PROTECTED_FOLDERS.some(f => path.includes(f.dir));
    return partial ? 'partial' : null;
}
/** "The path matches…", replaced only when the rule id proves the built-in folder rule. */
function ruleSentence(match, ruleId) {
    if (match.id === 'D11') {
        return ruleId != null && DEFAULT_ENV_FILE_PATTERNS.includes(ruleId)
            ? SECRET_FILE_ENV_RULE_SENTENCE
            : SECRET_FILE_RULE_SENTENCE;
    }
    if (match.id === 'D13') {
        return ruleId === `${PROTECTED_DIR_RULE_PREFIX}node-modules`
            ? SECRET_FILE_BUILTIN_RULE_SENTENCE.nodeModules
            : SECRET_FILE_RULE_SENTENCE;
    }
    const dir = match.id === 'D2' ? '.ssh/' : match.dir;
    const folder = PROTECTED_FOLDERS.find(f => f.dir === dir);
    return folder != null &&
        ruleId === `${PROTECTED_DIR_RULE_PREFIX}${folder.slug}`
        ? SECRET_FILE_BUILTIN_RULE_SENTENCE.folder.replace('{dir}', folder.dir)
        : SECRET_FILE_RULE_SENTENCE;
}
/** The phrase after the path in the row's "What happened" template. */
function roleTemplateOf(whatHappened) {
    return whatHappened.match(/involving \{path\}, (.+)\.$/)?.[1];
}
/** Row 77 template branch: the basename as received, case-sensitive. */
function isEnvTemplateFile(location) {
    const segments = location
        .trim()
        .replace(/\\/g, '/')
        .split('/')
        .filter(s => s.length > 0);
    const basename = segments[segments.length - 1] ?? '';
    return SECRET_FILE_ENV_TEMPLATE.basename.test(basename);
}
/** secretFilePatterns::secret-leakage (rows 67–80). */
export function resolveSecretFile(alert) {
    const location = present(alert.location);
    const generic = riskCopy('secretFilePatterns::secret-leakage#D3');
    if (location == null)
        return fallbackOf(generic);
    const match = selectSecretFileVariant(location, present(alert.ruleId));
    if (match == null || match === 'partial')
        return fallbackOf(generic);
    const copy = riskCopy(`secretFilePatterns::secret-leakage#${match.id}`);
    const folder = PROTECTED_FOLDERS.find(f => f.dir === match.dir);
    const facts = {
        // Rows 67–80: the normalized location as received (home already ~).
        path: location.trim(),
        dir: match.dir,
        folderExamples: folder?.examples,
        client: clientDisplayName(alert.clientType),
        // Row 78 (D12): empty → static fallback.
        ruleName: present(alert.ruleName),
        tool: present(alert.toolName) == null
            ? 'a tool call'
            : `the tool "${present(alert.toolName)}"`,
    };
    const envTemplate = match.id === 'D11' && isEnvTemplateFile(location);
    const role = envTemplate
        ? SECRET_FILE_ENV_TEMPLATE.role
        : fillRiskTemplate(roleTemplateOf(copy.whatHappened) ?? '', facts);
    const whatHappened = envTemplate
        ? copy.whatHappened.replace(/involving \{path\}, .+\.$/, `involving {path}, ${SECRET_FILE_ENV_TEMPLATE.role}.`)
        : copy.whatHappened;
    // Only pre-tool events with a known client name the client and tool; post
    // waits on V2 (which producers send file alerts after a tool ran).
    const whatTemplate = alert.eventKind === 'pre' && facts.client != null
        ? whatHappened
        : SECRET_FILE_NO_EVENT_WHAT;
    const what = role == null ? null : fillRiskTemplate(whatTemplate, { ...facts, role });
    const why = envTemplate
        ? SECRET_FILE_ENV_TEMPLATE.why
        : fillRiskTemplate(copy.whyItMatters.replace(SECRET_FILE_RULE_SENTENCE, ruleSentence(match, present(alert.ruleId))), facts);
    return {
        whatHappened: what ?? copy.fallbackWhatHappened,
        whyItMatters: why ?? copy.fallbackWhyItMatters,
    };
}
//# sourceMappingURL=secret-files.js.map